import { useMemo, useState } from "react";
import { AlertTriangle } from "lucide-react";

import { useLocalization } from "@i18n";

import {
  useSaveInspectionMutation,
  type InspectionConservationStateSelection,
  type InspectionDetail,
  type InspectionItemResponsePatch,
} from "../../../api/inspection";
import {
  buildInspectionEcColumns,
  ecItemKey,
  EC_SPECIAL_INSPECTION_SCORE,
} from "../../inspection-ec-columns";
import {
  useBindInspectionStepSave,
  type BindInspectionStepSave,
} from "../../../utils/useBindInspectionStepSave";

export interface InspectionConservationStateStepProps {
  structureId: string;
  inspectionId: string;
  /** Pré-preenchimento (Admin / última finalização) — form §7.4. */
  selections: InspectionConservationStateSelection[];
  canWrite: boolean;
  onSaved?: (detail: InspectionDetail) => void;
  onBindSave?: BindInspectionStepSave;
}

const InspectionConservationStateStep = ({
  structureId,
  inspectionId,
  selections,
  canWrite,
  onSaved,
  onBindSave,
}: InspectionConservationStateStepProps) => {
  const { translate } = useLocalization();
  const columns = useMemo(() => buildInspectionEcColumns(translate), [translate]);

  const [scores, setScores] = useState<Record<string, number>>(() =>
    Object.fromEntries(
      selections.map((selection) => [
        selection.columnKey,
        selection.selectedScore,
      ]),
    ),
  );
  const [isIncomplete, setIsIncomplete] = useState(false);

  const { mutate: saveInspection } = useSaveInspectionMutation(
    structureId,
    inspectionId,
  );

  const selected = columns.map((column) => scores[column.responseKey]);
  const isComplete = selected.every((score) => typeof score === "number");
  const total = selected.reduce((sum, score) => sum + (score ?? 0), 0);
  const requiresSpecialInspection = selected.some(
    (score) => score === EC_SPECIAL_INSPECTION_SCORE,
  );

  const handleSave = () => {
    if (!isComplete) {
      setIsIncomplete(true);
      return;
    }
    setIsIncomplete(false);

    const itemResponses: InspectionItemResponsePatch[] = columns.map(
      (column) => ({
        itemKey: ecItemKey(column.responseKey),
        occurrenceKey: "",
        answerValue: { selectedScore: scores[column.responseKey]! },
      }),
    );

    saveInspection(
      { itemResponses },
      { onSuccess: (detail) => onSaved?.(detail) },
    );
  };

  useBindInspectionStepSave(onBindSave, handleSave, canWrite);

  return (
    <div className="flex flex-col gap-3">
      {isIncomplete ? (
        <p role="alert" className="insp-step-error-banner">
          <AlertTriangle aria-hidden size={16} />
          {translate("inspection.form.stepIncomplete")}
        </p>
      ) : null}

      <p className="insp-ec-orient">
        <AlertTriangle aria-hidden size={16} className="shrink-0" />
        {translate("inspection.conservationState.orient")}
      </p>

      <div className="insp-ec-matrix-wrap">
      <div
        className="insp-ec-matrix"
        style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(150px, 1fr))` }}
      >
        {columns.map((column) => {
          const columnMissing =
            isIncomplete && typeof scores[column.responseKey] !== "number";

          return (
          <div
            key={`header-${column.key}`}
            data-testid={`ec-col-head-${column.responseKey}`}
            className={`insp-ec-col-head${columnMissing ? " is-invalid" : ""}`}
          >
            <p className="m-0">
              {column.title} ({column.letter})
            </p>
            {columnMissing ? (
              <p role="alert" className="insp-field-error">
                {translate("inspection.form.fieldErrorAnswer")}
              </p>
            ) : null}
          </div>
          );
        })}

        {Array.from(
          { length: Math.max(...columns.map((column) => column.options.length), 0) },
          (_, rowIndex) =>
            columns.map((column) => {
              const option = column.options[rowIndex];
              const cellKey = `${column.key}-row-${rowIndex}`;

              if (!option) {
                return <div key={cellKey} aria-hidden className="min-h-0" />;
              }

              const isSelected = scores[column.responseKey] === option.score;

              return (
                <button
                  key={cellKey}
                  type="button"
                  role="radio"
                  aria-label={`${column.key} ${option.score}`}
                  aria-checked={isSelected}
                  disabled={!canWrite}
                  onClick={() =>
                    setScores((current) => ({
                      ...current,
                      [column.responseKey]: option.score,
                    }))
                  }
                  className={[
                    "insp-ec-opt",
                    isSelected ? "is-sel" : "",
                    canWrite ? "cursor-pointer" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  <span className="insp-ec-opt-txt">{option.text}</span>
                  <span className="insp-ec-opt-score">
                    {String(option.score).padStart(2, "0")}
                  </span>
                </button>
              );
            }),
        )}
      </div>
      </div>

      <div className="insp-ec-sum-bar">
        <span className="insp-ec-sum-val">
          {translate("inspection.conservationState.sum")} = {total}
        </span>
      </div>

      {requiresSpecialInspection ? (
        <div className="insp-warn-box">
          <AlertTriangle aria-hidden size={18} className="shrink-0" />
          <p className="m-0">
            {translate("inspection.conservationState.specialWarnBefore")}
            <strong>
              {translate("inspection.conservationState.specialWarnEmphasis")}
            </strong>
            {translate("inspection.conservationState.specialWarnAfter")}
          </p>
        </div>
      ) : null}

      {canWrite && !onBindSave ? (
        <button
          type="button"
          className="insp-btn insp-btn-primary self-end"
          onClick={handleSave}
        >
          {translate("inspection.form.saveAndContinue")}
        </button>
      ) : null}
    </div>
  );
};

export default InspectionConservationStateStep;
